/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace triangular
*/
var triangular = {};

/**
* @name cdf
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/cdf}
*/
import cdf from './../../../../base/dists/triangular/cdf';
setReadOnly( triangular, 'cdf', cdf );

/**
* @name Triangular
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/ctor}
*/
import Triangular from './../../../../base/dists/triangular/ctor';
setReadOnly( triangular, 'Triangular', Triangular );

/**
* @name entropy
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/entropy}
*/
import entropy from './../../../../base/dists/triangular/entropy';
setReadOnly( triangular, 'entropy', entropy );

/**
* @name kurtosis
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/kurtosis}
*/
import kurtosis from './../../../../base/dists/triangular/kurtosis';
setReadOnly( triangular, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/logcdf}
*/
import logcdf from './../../../../base/dists/triangular/logcdf';
setReadOnly( triangular, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/logpdf}
*/
import logpdf from './../../../../base/dists/triangular/logpdf';
setReadOnly( triangular, 'logpdf', logpdf );

/**
* @name mean
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/mean}
*/
import mean from './../../../../base/dists/triangular/mean';
setReadOnly( triangular, 'mean', mean );

/**
* @name median
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/median}
*/
import median from './../../../../base/dists/triangular/median';
setReadOnly( triangular, 'median', median );

/**
* @name mgf
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/mgf}
*/
import mgf from './../../../../base/dists/triangular/mgf';
setReadOnly( triangular, 'mgf', mgf );

/**
* @name mode
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/mode}
*/
import mode from './../../../../base/dists/triangular/mode';
setReadOnly( triangular, 'mode', mode );

/**
* @name pdf
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/pdf}
*/
import pdf from './../../../../base/dists/triangular/pdf';
setReadOnly( triangular, 'pdf', pdf );

/**
* @name quantile
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/quantile}
*/
import quantile from './../../../../base/dists/triangular/quantile';
setReadOnly( triangular, 'quantile', quantile );

/**
* @name skewness
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/skewness}
*/
import skewness from './../../../../base/dists/triangular/skewness';
setReadOnly( triangular, 'skewness', skewness );

/**
* @name stdev
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/stdev}
*/
import stdev from './../../../../base/dists/triangular/stdev';
setReadOnly( triangular, 'stdev', stdev );

/**
* @name variance
* @memberof triangular
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/triangular/variance}
*/
import variance from './../../../../base/dists/triangular/variance';
setReadOnly( triangular, 'variance', variance );


// EXPORTS //

export default triangular;
